import axios from 'axios';
import Cookies from 'universal-cookie';
import { useOktaAuth } from './use-okta-auth';
import { eloquaTrackingUrl } from '../components/utilities/constants';
import { SetAuthHeader } from './utils/authHeader';
import { HandleAxiosError } from './utils/axiosError';
import { HandleEloquaError } from './utils/eloquaError';
import queryString from 'query-string';
import { createLocalStorageSync } from '../components/utilities/functions';

const pellaAPIURL = process.env.GATSBY_PELLA_API;
const pellaSiteURL = process.env.GATSBY_SITE_URL;
const environment = process.env.GATSBY_CONTENTSTACK_ENVIRONMENT;

const qs = require('qs');

const cookieManager = new Cookies();
const eloquaCookie = cookieManager.get('ELOQUA');
const eloquaGuid = eloquaCookie != null ? eloquaCookie.split('=').pop() : '';
const eloquaConfig = {
	headers: {
		'Content-Type': 'application/x-www-form-urlencoded',
	},
};

let params = {};

const cartSync = createLocalStorageSync('cartSession');

export const useCart = () => {
	const { authState, profile } = useOktaAuth();
	const accessToken = authState?.accessToken?.accessToken;

	const getCart = async (token = null, getLeadTimes = false, postalCode = null) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		const region_id = queryString.parse(window.location.search).region_id;

		try {
			const requestConfig = {
				headers: {
					...SetAuthHeader(accessToken, token),
				},
				withCredentials: true,
			};

			params.client_postal_code = postalCode || userPostalCode || region_id;

			if (getLeadTimes) {
				params.include_lead_times = true;
			}

			const response = await axios.get(`${pellaAPIURL}/web-ecommerce/api/v1/carts`, { ...requestConfig, params: params });
			if (response?.data?.Cart) {
				if (response.data.Cart?.total_number) {
					cartSync.set(response.data.Cart);
				} else {
					cartSync.set({});
				}
				return response.data;
			} else {
				cartSync.set({});
				return {
					Cart: {},
					error: {
						message: 'Customer does not have an active cart',
					},
				};
			}
		} catch (error) {
			cartSync.set({});
			return HandleAxiosError(error);
		}
	};

	const addItemToCart = async (configKey, url, getLeadTimes, bundle) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const requestConfig = {
				headers: {
					...SetAuthHeader(accessToken),
					'Content-Type': 'application/json',
				},
				withCredentials: true,
			};

			const body = {
				item_url: url,
			};

			if (bundle?.bundleID) {
				body.bundles = [
					{
						backend_name: bundle.bundleID,
						quantity: bundle.quantity,
						item_urls: [
							{ type: 'part', url: `${pellaSiteURL}/shop/parts/{backend_name}/?item={backend_name}` },
							{ type: 'window', url: `${pellaSiteURL}/shop/parts/configure/?item={backend_name}` },
							{ type: 'patio-door', url: `${pellaSiteURL}/shop/parts/configure/?item={backend_name}` },
							{ type: 'entry-door', url: `${pellaSiteURL}/shop/parts/configure/?item={backend_name}` },
						],
					},
				];
			}

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			if (getLeadTimes) {
				params.include_lead_times = true;
			}

			const response = await axios.post(`${pellaAPIURL}/web-ecommerce/api/v1/carts/items/${configKey}`, body, { ...requestConfig, params: params });

			if (response?.data?.Cart) {
				cartSync.set(response.data.Cart);

				if (eloquaGuid && profile?.identifier) {
					try {
						const body = {
							elqFormName: 'cartUpdated',
							elqSiteID: '1920192983',
							elqCustomerGUID: eloquaGuid,
							elqCookieWrite: '0',
							emailAddress: profile.identifier,
							action: 'add',
							count: response.data.Cart?.total_number || 0,
							env: environment,
						};

						await axios.post(eloquaTrackingUrl, qs.stringify(body), eloquaConfig);
					} catch (error) {
						HandleEloquaError(error);
					}
				}
			}

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const updateCartItem = async (itemID, quantity, configKey, url, getLeadTimes) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const requestConfig = {
				headers: {
					...SetAuthHeader(accessToken),
					'Content-Type': 'application/json',
				},
				withCredentials: true,
			};

			const requestData = {
				quantity: quantity,
				config_id: configKey,
				item_url: url,
			};

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			if (getLeadTimes) {
				params.include_lead_times = true;
			}

			const response = await axios.put(`${pellaAPIURL}/web-ecommerce/api/v1/carts/items/${itemID}`, requestData, { ...requestConfig, params: params });

			if (response?.data?.Cart) {
				cartSync.set(response.data.Cart);
			} else {
				cartSync.set({});
			}

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const deleteCartItem = async (itemID, getLeadTimes) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const requestConfig = {
				headers: {
					...SetAuthHeader(accessToken),
					'Content-Type': 'application/json',
				},
				withCredentials: true,
			};

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			if (getLeadTimes) {
				params.include_lead_times = true;
			}

			const response = await axios.delete(`${pellaAPIURL}/web-ecommerce/api/v1/carts/items/${itemID}`, { ...requestConfig, params: params });

			if (response?.data?.Cart) {
				cartSync.set(response.data.Cart);

				if (eloquaGuid && profile?.identifier) {
					try {
						const body = {
							elqFormName: 'cartUpdated',
							elqSiteID: '1920192983',
							elqCustomerGUID: eloquaGuid,
							elqCookieWrite: '0',
							emailAddress: profile.identifier,
							action: 'delete',
							count: response.data.Cart?.total_number || 0,
							env: environment,
						};

						await axios.post(eloquaTrackingUrl, qs.stringify(body), eloquaConfig);
					} catch (error) {
						HandleEloquaError(error);
					}
				}
			}

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const addBundleToCart = async (bundleID, quantity, parent_id) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const requestConfig = {
				headers: {
					...SetAuthHeader(accessToken),
					'Content-Type': 'application/json',
				},
				withCredentials: true,
			};
			const body = {
				item_urls: [
					{ type: 'part', url: `${pellaSiteURL}/shop/parts/{backend_name}/?item={backend_name}` },
					{ type: 'window', url: `${pellaSiteURL}/shop/parts/configure/?item={backend_name}` },
					{ type: 'patio-door', url: `${pellaSiteURL}/shop/parts/configure/?item={backend_name}` },
					{ type: 'entry-door', url: `${pellaSiteURL}/shop/parts/configure/?item={backend_name}` },
				],
				quantity: quantity,
			};

			if (parent_id) body.parent_id = parent_id;

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.post(`${pellaAPIURL}/web-ecommerce/api/v1/carts/bundles/${bundleID}`, body, { ...requestConfig, params: params });

			if (response?.data?.Cart) {
				cartSync.set(response.data.Cart);

				if (eloquaGuid && profile?.identifier) {
					try {
						const body = {
							elqFormName: 'cartUpdated',
							elqSiteID: '1920192983',
							elqCustomerGUID: eloquaGuid,
							elqCookieWrite: '0',
							emailAddress: profile.identifier,
							action: 'add',
							count: response.data.Cart?.total_number || 0,
							env: environment,
						};

						await axios.post(eloquaTrackingUrl, qs.stringify(body), eloquaConfig);
					} catch (error) {
						HandleEloquaError(error);
					}
				}
			} else {
				cartSync.set({});
			}

			return response?.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const getDeliveryOptionDetails = async (job_site_address) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const requestData = {
				job_site_address: {
					line_1: job_site_address.line_1,
					city: job_site_address.city,
					state: job_site_address.state,
					postcode: job_site_address.postcode,
					country: job_site_address.country,
				},
			};

			const requestConfig = {
				headers: {
					...SetAuthHeader(accessToken),
					'Content-Type': 'application/json',
				},
				withCredentials: true,
			};

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.post(`${pellaAPIURL}/web-ecommerce/api/v1/delivery/details`, requestData, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const copyItemFromCart = async (sku, targetData, clientID = 'H03') => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const requestConfig = {
				headers: {
					...SetAuthHeader(accessToken),
				},
				withCredentials: true,
			};

			if (clientID) {
				params.clientID = clientID;
			}

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.post(`${pellaAPIURL}/web-ecommerce/api/v1/carts/items/${sku}/copy`, targetData, { ...requestConfig, params: params });

			return response;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	return {
		getCart,
		addItemToCart,
		updateCartItem,
		deleteCartItem,
		addBundleToCart,
		getDeliveryOptionDetails,
		copyItemFromCart,
	};
};
